"use client";

import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import type { Contact } from "@/lib/types/database";
import { useGlobalModalStack, useGlobalDrawerStack, MODAL_IDS } from "@wtc/ui";
import { useMediaQuery } from "@/lib/hooks/ui/use-media-query";

// Generic components
import { UniversalEntityPage } from "@/components/shared/UniversalEntityPage";
import { useTaskHooks } from "@/lib/hooks/api/shared/graphql-hooks";

// Task-specific components
import { getEntityTableConfig } from "@/lib/entity-registry";
import type { Contact_Tasks } from "@/src/gql/graphql";

const TASK_CONFIG = getEntityTableConfig<Contact_Tasks>("tasks");
import { TaskTemplateSelector } from "@/features/tasks/components/TaskTemplateSelector";
import { SaveTemplateModal } from "@/features/tasks/components/SaveTemplateModal";

// Types
import type { EntityFormProps } from "@/components/shared/UniversalEntityPage/types";

interface ContactTasksProps {
  client: Contact;
  className?: string;
}

export const ContactTasks: React.FC<ContactTasksProps> = ({ client, className = "" }) => {
  // Call hooks at the top level first
  const taskHooks = useTaskHooks();
  const globalModalStack = useGlobalModalStack();
  const drawerStack = useGlobalDrawerStack();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Entity form props for template support
  const entityFormProps: EntityFormProps<Contact_Tasks> = useMemo(
    () => ({
      templateSelector: TaskTemplateSelector,
      saveTemplateModal: SaveTemplateModal,
    }),
    []
  );

  // Create a custom config that filters by client
  const clientTaskConfig = useMemo(
    () => ({
      ...TASK_CONFIG,
      // Add client-specific filters using generic entity pattern
      defaultFilters: {
        entity_type: "contacts",
        entity_id: client.id,
      },
      // Override display name for context
      displayName: `Tasks for ${client.name}`,
    }),
    [client.id, client.name]
  );

  // Custom header component with stats and create button
  const ClientTasksHeader = () => {
    return (
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Tasks for {client.name}</h3>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              Client: {client.name}
            </Badge>
            {client.company_name && (
              <Badge variant="outline" className="text-xs">
                Company: {client.company_name}
              </Badge>
            )}
          </div>
        </div>

        {/* Create Task Button */}
        <div className="flex items-center gap-3">
          <Button
            onClick={() => {
              if (isMobile) {
                // Use drawer stack for mobile
                drawerStack.openDrawer("create-tasks-drawer", {
                  title: "Create New Task",
                  description: "Create a new task",
                  side: "bottom",
                  height: "95vh",
                  enableSwipeGesture: true,
                  showCloseButton: true,
                });
              } else {
                // Use global modal stack for desktop
                globalModalStack.openModal(MODAL_IDS.tasks.create);
              }
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4" />
            Create Task
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className={className}>
      <ClientTasksHeader />

      {/* Use the generic UniversalEntityPage with client-specific configuration */}
      <UniversalEntityPage
        entityName="tasks"
        config={clientTaskConfig}
        hooks={taskHooks}
        filterPreset="all"
        title={`Tasks for ${client.name}`}
        description={`Manage tasks for ${client.name}`}
        storageKey={`client-${client.id}-tasks-column-preferences`}
        // Pass client filter to the hooks using generic entity pattern
        customFilters={{
          entity_type: "contacts",
          entity_id: client.id,
        }}
        entityFormProps={entityFormProps}
        // Hide the default header since we have a custom one
        showHeader={false}
      />
    </div>
  );
};

export { ContactTasks as ClientTasks };
